import { Component, Input, signal, Signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AppIconComponent, IconColor, IconSize } from './app-icon.component';

export interface StatItem {
  title: string;
  subtitle: string;
  value: string | number;
  icon: string;
  color: 'primary' | 'accent' | 'warn';
  change: string;
  changeIcon: string;
  changeClass: 'positive' | 'negative' | 'neutral';
}

/**
 * AppStatsCardComponent - Microcomponente para cards de estatísticas
 * Substitui mat-card em contextos de métricas e estatísticas
 *
 * @example
 * <app-stats-card [stats]="stats" />
 */
@Component({
  selector: 'app-stats-card',
  standalone: true,
  imports: [CommonModule, AppIconComponent],
  template: `
    <div
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-6"
    >
      @for (stat of stats(); track stat.title) {
      <div
        class="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200"
      >
        <!-- Header -->
        <div class="flex items-center mb-4">
          <div
            [class]="getAvatarClass(stat.color)"
            class="w-12 h-12 rounded-lg flex items-center justify-center mr-4"
          >
            <app-icon
              [name]="stat.icon"
              [color]="getIconColor(stat.color)"
              [size]="24"
            />
          </div>
          <div class="flex-1">
            <h3 class="text-sm font-semibold text-gray-900 mb-1">
              {{ stat.title }}
            </h3>
            <p class="text-xs text-gray-600">{{ stat.subtitle }}</p>
          </div>
        </div>

        <!-- Content -->
        <div class="flex justify-between items-end">
          <div class="text-2xl font-bold text-gray-900">{{ stat.value }}</div>
          <div
            [class]="getChangeClass(stat.changeClass)"
            class="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg"
          >
            <app-icon
              [name]="stat.changeIcon"
              [color]="getChangeIconColor(stat.changeClass)"
              [size]="16"
            />
            <span>{{ stat.change }}</span>
          </div>
        </div>
      </div>
      }
    </div>
  `,
  styles: [],
})
export class AppStatsCardComponent {
  @Input() stats: Signal<StatItem[]> = signal<StatItem[]>([]);

  getAvatarClass(color: string): string {
    const baseClass =
      'w-12 h-12 rounded-lg flex items-center justify-center mr-4';
    switch (color) {
      case 'primary':
        return `${baseClass} bg-blue-100`;
      case 'accent':
        return `${baseClass} bg-green-100`;
      case 'warn':
        return `${baseClass} bg-red-100`;
      default:
        return `${baseClass} bg-gray-100`;
    }
  }

  getIconColor(color: string): IconColor {
    switch (color) {
      case 'primary':
        return 'primary';
      case 'accent':
        return 'success';
      case 'warn':
        return 'error';
      default:
        return 'gray';
    }
  }

  getChangeClass(changeClass: string): string {
    const baseClass =
      'flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg';
    switch (changeClass) {
      case 'positive':
        return `${baseClass} text-green-700 bg-green-100`;
      case 'negative':
        return `${baseClass} text-red-700 bg-red-100`;
      case 'neutral':
        return `${baseClass} text-gray-700 bg-gray-100`;
      default:
        return `${baseClass} text-gray-700 bg-gray-100`;
    }
  }

  getChangeIconColor(changeClass: string): IconColor {
    switch (changeClass) {
      case 'positive':
        return 'success';
      case 'negative':
        return 'error';
      case 'neutral':
        return 'gray';
      default:
        return 'gray';
    }
  }
}
